/**
 * App timezone: Asia/Bangkok (UTC+7).
 * All event dates/times in the app (schedule, admin, tickets) use Bangkok.
 * Storage and API remain UTC; display and day-grouping use Bangkok.
 */

export const BANGKOK_TZ = "Asia/Bangkok";

/** Date key "YYYY-MM-DD" for grouping/filtering by calendar day in Bangkok */
export type BangkokDateKey = string;

/**
 * Get calendar date in Bangkok for an ISO instant.
 * Used to group events by day in schedule.
 */
export function getDateKeyBangkok(iso: string): BangkokDateKey {
  const d = new Date(iso);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: BANGKOK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "0";
  const pad = (s: string) => s.padStart(2, "0");
  return `${get("year")}-${pad(get("month"))}-${pad(get("day"))}`;
}

/** Today's date key in Bangkok (for "today" in schedule). */
export function getTodayDateKeyBangkok(): BangkokDateKey {
  return getDateKeyBangkok(new Date().toISOString());
}

/**
 * Start of day in Bangkok as UTC Date.
 * Used for date range filters (e.g. analytics "today" = Bangkok day boundaries in UTC).
 */
export function startOfDayBangkokUTC(dateKey: BangkokDateKey): Date {
  return new Date(`${dateKey}T00:00:00+07:00`);
}

/**
 * End of day in Bangkok as UTC Date (23:59:59.999).
 */
export function endOfDayBangkokUTC(dateKey: BangkokDateKey): Date {
  return new Date(`${dateKey}T23:59:59.999+07:00`);
}

/** Monday of the current week in Bangkok (date key). */
export function getWeekStartDateKeyBangkok(): BangkokDateKey {
  const today = getTodayDateKeyBangkok();
  const [y, m, d] = today.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const weekday = date.getDay();
  const daysFromMonday = (weekday + 6) % 7;
  return addDaysToDateKey(today, -daysFromMonday);
}

/** Add days to a date key (YYYY-MM-DD). */
export function addDaysToDateKey(
  dateKey: BangkokDateKey,
  days: number,
): BangkokDateKey {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Format time in Bangkok (e.g. "14:00") from ISO string. */
export function formatTimeBangkok(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("ru-RU", {
    timeZone: BANGKOK_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Format date key as short date in Bangkok (e.g. "5 февраля") for schedule. */
export function formatDateShortBangkok(dateKey: BangkokDateKey): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("ru-RU", {
    month: "long",
    day: "numeric",
  });
}

/** Day of month (1–31) and weekday index (0=Mon … 6=Sun) for a date key. */
export function getDayOfMonthAndWeekdayBangkok(
  dateKey: BangkokDateKey,
): { dayOfMonth: number; weekdayIndex: number } {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const weekdayIndex = (date.getDay() + 6) % 7;
  return { dayOfMonth: d, weekdayIndex };
}

/**
 * Format UTC ISO string for datetime-local input: show time in Bangkok.
 * Input: ISO from API (e.g. "2026-02-14T06:00:00.000Z")
 * Output: "YYYY-MM-DDTHH:mm" in Bangkok (e.g. "2026-02-14T13:00")
 */
export function formatDateForInputBangkok(iso: string): string {
  const d = new Date(iso);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: BANGKOK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "0";
  const pad = (s: string) => s.padStart(2, "0");
  return `${get("year")}-${pad(get("month"))}-${pad(get("day"))}T${pad(get("hour"))}:${pad(get("minute"))}`;
}

/**
 * Interpret datetime-local value as Bangkok time and return ISO string with offset.
 * Input: "YYYY-MM-DDTHH:mm" (value from input, meaning Bangkok local)
 * Output: "YYYY-MM-DDTHH:mm:ss+07:00" for API
 */
export function datetimeLocalToBangkokISO(localValue: string): string {
  if (!localValue || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localValue)) {
    return localValue;
  }
  return `${localValue}:00+07:00`;
}

/**
 * Format UTC ISO string for display in admin: date/time in Bangkok, ru-RU locale.
 */
export function formatDateBangkok(
  iso: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", {
    timeZone: BANGKOK_TZ,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}
